import React, { useState } from "react";

import axios from "axios";
import { useTranslation } from "react-i18next";

import { Button, IconButton, Loader, showNotification } from "../index";
import { DownloadIcon, CheckIcon } from "../Icons";

import styles from "./ExportPictusJson.module.scss";

interface ExportParams {
  requestParams: any;
}

interface Props {
  buttonId: string;
  buttonLabel: string;
  isIconButton?: boolean;
  getExportParams: () => ExportParams;
  userEmail: string;
  onSuccess?: () => void;
  onError?: (err: any) => void;
  disabled?: boolean;
}

const NOOP_FUNC = () => {};

/**
 * Component for exporting transactions in Pictus JSON format
 * 
 * Features:
 * - Sends request parameters to backend for async processing
 * - Backend validates request, filters transactions, and generates JSON file
 * - JSON file will be emailed to user when ready
 * - Shows success notification about email
 * 
 * Usage:
 * <ExportPictusJson
 *   buttonId="export-pictus-json-button"
 *   buttonLabel="Export Transaction JSON"
 *   getExportParams={getExportParams}
 * />
 */
const ExportPictusJson: React.FC<Props> = ({
  buttonId,
  buttonLabel,
  isIconButton = false,
  getExportParams,
  userEmail,
  onSuccess = NOOP_FUNC,
  onError = NOOP_FUNC,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const exportData = async () => {
    setIsLoading(true);

    try {
      // Get export parameters
      const { requestParams } = getExportParams();

      await axios.post("/transactions/exportPictusJson", requestParams);

      // Show success notification about email
      showNotification(
        t("transactions.grid.export.pictusJsonEmailNotification", { email: userEmail }),
        undefined,
        CheckIcon
      );

      onSuccess();
      setIsLoading(false);
    } catch (err) {
      onError(err);
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loader className={styles.inlineLoader} fontSize={20} />}
      {isIconButton ? (
        <IconButton
          hasTooltip
          id={buttonId}
          disabled={disabled || isLoading}
          onClick={exportData}
          aria-label={buttonLabel}
          className={styles.iconButton}
        >
          <DownloadIcon fillColor={null} />
        </IconButton>
      ) : (
        <Button
          id={buttonId}
          variant="inline"
          disabled={disabled || isLoading}
          onClick={exportData}
          className={styles.button}
          iconComponent={DownloadIcon}
        >
          {buttonLabel}
        </Button>
      )}
    </>
  );
};

export default ExportPictusJson;
